"use client"

import { classNames, FlexCenter } from "@opensea/ui-kit"
import dynamic from "next/dynamic"
import { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { getChestRiveFile } from "./chest-level-mapping"

interface RiveInput {
  name: string
  fire?: () => void
}

interface RiveInstance {
  stateMachineInputs: (name: string) => RiveInput[] | null
}

export interface RiveChestHandle {
  fireInput: (inputName: string) => void
}

interface RiveChestProps {
  level: number
  stateMachines?: string | string[]
  width?: number | string
  height?: number | string
  className?: string
  onReady?: () => void
}

const LazyRiveWrapper = dynamic(
  () =>
    import("@rive-app/react-canvas").then(({ useRive }) => {
      const RiveChestInner = forwardRef<
        RiveChestHandle,
        {
          src: string
          stateMachines?: string | string[]
          onReady?: () => void
        }
      >(({ src, stateMachines, onReady }, ref) => {
        const [hasError, setHasError] = useState(false)
        const [isReady, setIsReady] = useState(false)
        const riveInstanceRef = useRef<RiveInstance | null>(null)

        const { rive, RiveComponent } = useRive({
          src,
          stateMachines,
          autoplay: true,
          onLoad: () => {
            setHasError(false)
            setIsReady(true)
            riveInstanceRef.current = rive as RiveInstance
            onReady?.()
          },
          onLoadError: error => {
            console.error("[RiveChest] Failed to load animation:", error)
            setHasError(true)
          },
        })

        useImperativeHandle(
          ref,
          () => ({
            fireInput: (inputName: string) => {
              if (!rive) {
                return
              }

              const stateMachineName = Array.isArray(stateMachines)
                ? stateMachines[0]
                : stateMachines

              try {
                const inputs = rive.stateMachineInputs(stateMachineName || "")
                const triggerInput = inputs?.find(
                  (input: RiveInput) => input.name === inputName,
                )

                triggerInput?.fire?.()
              } catch (error) {
                console.warn("[RiveChest] Failed to fire input:", error)
              }
            },
          }),
          [rive, stateMachines],
        )

        if (hasError) {
          return null
        }

        return (
          <div className={classNames("size-full", !isReady && "opacity-0")}>
            <RiveComponent className="size-full" />
          </div>
        )
      })

      RiveChestInner.displayName = "RiveChestInner"
      return { default: RiveChestInner }
    }),
  {
    loading: () => null,
    ssr: false,
  },
)

export const RiveChest = forwardRef<RiveChestHandle, RiveChestProps>(
  ({ level, stateMachines, width, height, className, onReady }, ref) => {
    const src = getChestRiveFile(level)

    return (
      <FlexCenter
        className={classNames("shrink-0 justify-center", className)}
        style={{ width, height }}
      >
        <LazyRiveWrapper
          onReady={onReady}
          ref={ref}
          src={src}
          stateMachines={stateMachines}
        />
      </FlexCenter>
    )
  },
)

RiveChest.displayName = "RiveChest"
